import { useEffect, useState, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { supabase } from '@/lib/supabaseClient';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';

export type Phase = {
  id_fase?: string;
  nombre_fase: string;
  duracion_minutos: number;
  requiere_atencion: boolean;
  orden: number;
};

export const useFasesServicio = (serviceId: string, open: boolean) => {
  const [phases, setPhases] = useState<Phase[]>([]);
  const [loading, setLoading] = useState(false);

  const idUsuario =
    typeof window !== 'undefined'
      ? localStorage.getItem('id_usuario') || ''
      : '';

  // ============================
  // Cargar fases del servicio
  // ============================
  const fetchPhases = useCallback(async () => {
    if (!serviceId) return;

    setLoading(true);

    const { data, error } = await supabase
      .from('fases_servicio')
      .select('*')
      .eq('id_servicio', serviceId)
      .order('orden', { ascending: true });

    if (error) {
      Sentry.captureException(error, { extra: { serviceId } });
      toast.error('Error al cargar fases');
    } else {
      setPhases(data || []);
    }

    setLoading(false);
  }, [serviceId]);

  useEffect(() => {
    if (open) fetchPhases();
  }, [open, fetchPhases]);

  // ============================
  // Editar campo de una fase
  // ============================
  const updateField = (index: number, field: keyof Phase, value: any) => {
    setPhases((prev) =>
      prev.map((phase, i) =>
        i === index ? { ...phase, [field]: value } : phase
      )
    );
  };

  // ============================
  // Añadir fase nueva
  // ============================
  const addPhase = () => {
    setPhases((prev) => [
      ...prev,
      {
        id_fase: uuidv4(),
        nombre_fase: '',
        duracion_minutos: 0,
        requiere_atencion: true,
        orden: prev.length + 1,
      },
    ]);
  };

  // ============================
  // Eliminar fase (local)
  // ============================
  const removePhase = (index: number) => {
    setPhases((prev) => prev.filter((_, i) => i !== index));
  };

  // ============================
  // Guardar todas las fases
  // ============================
  const save = async () => {
    if (!idUsuario) {
      toast.error('Sesión no válida');
      return false;
    }

    setLoading(true);

    const { error: deleteError } = await supabase
      .from('fases_servicio')
      .delete()
      .eq('id_servicio', serviceId);

    if (deleteError) {
      Sentry.captureException(deleteError, { extra: { serviceId } });
      toast.error('Error al eliminar fases anteriores');
      setLoading(false);
      return false;
    }

    const { error: insertError } = await supabase.from('fases_servicio').insert(
      phases.map((p, index) => ({
        id_fase: p.id_fase || uuidv4(),
        id_servicio: serviceId,
        nombre_fase: p.nombre_fase,
        duracion_minutos: p.duracion_minutos,
        requiere_atencion: p.requiere_atencion,
        orden: index + 1,
        usuario_creacion: idUsuario,
        activo: true,
      }))
    );

    setLoading(false);

    if (insertError) {
      Sentry.captureException(insertError, { extra: { serviceId } });
      toast.error('Error al guardar fases');
      return false;
    }

    toast.success('Fases actualizadas correctamente');
    return true;
  };

  return {
    phases,
    loading,
    addPhase,
    removePhase,
    updateField,
    save,
    fetchPhases,
  };
};
